import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { viaggiApi, routeApi, favoriteRoutesApi } from '../lib/api';
import { ImageLightbox } from '../components/ImageLightbox';
import { RouteMap } from '../components/RouteMap';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Map, Plus, Trash2, Loader2, MapPin, ChevronDown, ChevronUp, Pencil, 
  Navigation, Wallet, Route, Search, Clock, Fuel, ZoomIn, Calculator, Star, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { FotoUploader } from '../components/FotoUploader';
import { ListSkeleton, ViaggioCardSkeleton } from '../components/Skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VALUTE = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CHF', symbol: 'CHF', name: 'Franco Svizzero' },
  { code: 'USD', symbol: '$', name: 'Dollaro USA' },
];

// Fuel cost per km estimates (in EUR)
const FUEL_COST_PER_KM = 0.08; // Average motorcycle

export default function Viaggi() {
  const { t } = useTranslation();
  const { user, motoList, selectedMotoId } = useAuth();
  const [viaggi, setViaggi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedViaggio, setExpandedViaggio] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Route planner state
  const [startQuery, setStartQuery] = useState('');
  const [endQuery, setEndQuery] = useState('');
  const [startResults, setStartResults] = useState([]);
  const [endResults, setEndResults] = useState([]);
  const [startCoords, setStartCoords] = useState(null);
  const [endCoords, setEndCoords] = useState(null);
  const [routeResult, setRouteResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [searchingStart, setSearchingStart] = useState(false);
  const [searchingEnd, setSearchingEnd] = useState(false);
  
  // Waypoints state
  const [waypoints, setWaypoints] = useState([]);
  const [waypointQuery, setWaypointQuery] = useState('');
  const [waypointResults, setWaypointResults] = useState([]);
  const [searchingWaypoint, setSearchingWaypoint] = useState(false);
  const [avoidHighways, setAvoidHighways] = useState(false);
  
  // Favorite routes state
  const [favoriteRoutes, setFavoriteRoutes] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [savingFavorite, setSavingFavorite] = useState(false);
  const [favoriteName, setFavoriteName] = useState('');

  const [formData, setFormData] = useState({
    moto_id: '',
    titolo: '',
    partenza: '',
    arrivo: '',
    km_percorsi: 0,
    durata_ore: 0,
    data_partenza: '',
    data_arrivo: '',
    costo: null,
    valuta: user?.valuta || 'CHF',
    foto_urls: [],
    diario: '',
    note: ''
  });

  useEffect(() => {
    if (motoList.length > 0) {
      loadViaggi();
    } else {
      setLoading(false);
    }
  }, [selectedMotoId, motoList]);

  // Load favorite routes when planner opens
  useEffect(() => {
    if (plannerOpen) {
      loadFavoriteRoutes();
    }
  }, [plannerOpen]);

  const loadViaggi = async () => {
    try {
      setLoading(true);
      const response = await viaggiApi.getAll(selectedMotoId || undefined);
      setViaggi(response.data);
    } catch (error) {
      toast.error(t('errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadFavoriteRoutes = async () => {
    try {
      const response = await favoriteRoutesApi.getAll();
      setFavoriteRoutes(response.data);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const saveFavoriteRoute = async () => {
    if (!routeResult || !startQuery || !endQuery) return;
    
    const name = favoriteName.trim() || `${startQuery.split(',')[0]} → ${endQuery.split(',')[0]}`;
    
    setSavingFavorite(true);
    try {
      await favoriteRoutesApi.create({
        nome: name,
        start_name: startQuery,
        start_coords: startCoords,
        end_name: endQuery,
        end_coords: endCoords,
        distance_km: routeResult.distance_km,
        duration_hours: routeResult.duration_hours
      });
      toast.success(t('trips.planner.favoriteSaved'));
      setFavoriteName('');
      loadFavoriteRoutes();
    } catch (error) {
      toast.error(t('errors.saveError'));
    } finally {
      setSavingFavorite(false);
    }
  };

  const deleteFavoriteRoute = async (id) => {
    try {
      await favoriteRoutesApi.delete(id);
      toast.success(t('success.deleted'));
      loadFavoriteRoutes();
    } catch (error) {
      toast.error(t('errors.deleteError'));
    }
  };

  const loadFavoriteToPlanner = async (fav) => {
    setStartQuery(fav.start_name);
    setStartCoords(fav.start_coords);
    setEndQuery(fav.end_name);
    setEndCoords(fav.end_coords);
    setShowFavorites(false);
    
    // Auto-calculate the route
    setCalculating(true);
    try {
      const response = await routeApi.calculate({
        start_coords: fav.start_coords,
        end_coords: fav.end_coords
      });
      setRouteResult(response.data);
    } catch (error) {
      toast.error(t('trips.planner.calcError'));
    } finally {
      setCalculating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      moto_id: selectedMotoId || (motoList[0]?.id || ''),
      titolo: '',
      partenza: '',
      arrivo: '',
      km_percorsi: 0,
      durata_ore: 0,
      data_partenza: new Date().toISOString().split('T')[0],
      data_arrivo: '',
      costo: null,
      valuta: user?.valuta || 'CHF',
      foto_urls: [],
      diario: '',
      note: ''
    });
    setEditingId(null);
  };

  const handleOpenDialog = (viaggio = null) => {
    if (viaggio) {
      setFormData({
        moto_id: viaggio.moto_id,
        titolo: viaggio.titolo,
        partenza: viaggio.partenza,
        arrivo: viaggio.arrivo,
        km_percorsi: viaggio.km_percorsi,
        durata_ore: viaggio.durata_ore,
        data_partenza: viaggio.data_partenza,
        data_arrivo: viaggio.data_arrivo || '',
        costo: viaggio.costo,
        valuta: viaggio.valuta || user?.valuta || 'CHF',
        foto_urls: viaggio.foto_urls || [],
        diario: viaggio.diario || '',
        note: viaggio.note || ''
      });
      setEditingId(viaggio.id);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titolo || !formData.moto_id) {
      toast.error(t('trips.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await viaggiApi.update(editingId, formData);
        toast.success(t('success.updated'));
      } else {
        await viaggiApi.create(formData);
        toast.success(t('success.saved'));
      }
      setDialogOpen(false);
      resetForm();
      await loadViaggi();
    } catch (error) {
      toast.error(t('errors.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('trips.deleteConfirm'))) return;
    try {
      await viaggiApi.delete(id);
      toast.success(t('success.deleted'));
      await loadViaggi();
    } catch (error) {
      toast.error(t('errors.deleteError'));
    }
  };

  // Route Planner Functions - Global search without country filter
  const searchLocation = useCallback(async (query, type) => {
    if (!query || query.length < 3) {
      if (type === 'start') setStartResults([]);
      else if (type === 'end') setEndResults([]);
      else setWaypointResults([]);
      return;
    }

    if (type === 'start') setSearchingStart(true);
    else if (type === 'end') setSearchingEnd(true);
    else setSearchingWaypoint(true);

    try {
      const response = await routeApi.geocode({ query });
      if (type === 'start') setStartResults(response.data);
      else if (type === 'end') setEndResults(response.data);
      else setWaypointResults(response.data);
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      if (type === 'start') setSearchingStart(false);
      else if (type === 'end') setSearchingEnd(false);
      else setSearchingWaypoint(false);
    }
  }, []);

  const selectLocation = (result, type) => {
    if (type === 'start') {
      setStartQuery(result.name);
      setStartCoords(result.coordinates);
      setStartResults([]);
    } else if (type === 'end') {
      setEndQuery(result.name);
      setEndCoords(result.coordinates);
      setEndResults([]);
    } else {
      // Add waypoint
      setWaypoints([...waypoints, { name: result.name, coords: result.coordinates }]);
      setWaypointQuery('');
      setWaypointResults([]);
    }
  };

  const removeWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };

  const calculateRoute = async () => {
    if (!startCoords || !endCoords) {
      toast.error(t('trips.planner.selectBoth'));
      return;
    }

    setCalculating(true);
    try {
      const response = await routeApi.calculate({
        start_coords: startCoords,
        end_coords: endCoords,
        waypoints: waypoints.length > 0 ? waypoints.map(w => w.coords) : undefined,
        avoid_highways: avoidHighways
      });
      setRouteResult(response.data);
    } catch (error) {
      toast.error(t('trips.planner.calcError'));
    } finally {
      setCalculating(false);
    }
  };

  const applyRouteToForm = () => {
    if (!routeResult) return;
    
    setFormData(prev => ({
      ...prev,
      partenza: startQuery,
      arrivo: endQuery,
      km_percorsi: Math.round(routeResult.distance_km),
      durata_ore: routeResult.duration_hours,
      costo: Math.round(routeResult.distance_km * FUEL_COST_PER_KM * 100) / 100
    }));
    setPlannerOpen(false);
    setDialogOpen(true);
    toast.success(t('trips.planner.applied'));
  };

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getImageUrl = (url) => {
    return url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const openGoogleMaps = (partenza, arrivo) => {
    const url = `https://www.google.com/maps/dir/${encodeURIComponent(partenza)}/${encodeURIComponent(arrivo)}`;
    window.open(url, '_blank');
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('de-CH').format(num || 0);
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return null;
    const symbols = { CHF: 'CHF', EUR: '€', USD: '$' };
    return `${symbols[currency] || currency} ${formatNumber(amount)}`;
  };

  const getMotoName = (motoId) => {
    const moto = motoList.find(m => m.id === motoId);
    return moto ? `${moto.marca} ${moto.modello}` : '';
  };

  if (motoList.length === 0) {
    return (
      <Layout>
        <div className="page-container">
          <div className="vault-card text-center py-12">
            <Map className="w-16 h-16 text-[#FF5F1F]/30 mx-auto mb-4" />
            <p className="text-[#9CA3AF] text-lg mb-2">{t('trips.addMotoFirst')}</p>
            <p className="text-[#6B7280] text-sm">{t('trips.needMotoForTrips')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header flex-col sm:flex-row gap-4">
          <div>
            <h1 className="page-title">{t('nav.trips')}</h1>
            <p className="page-subtitle">{t('trips.subtitle')}</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            {/* Route Planner Button */}
            <Dialog open={plannerOpen} onOpenChange={setPlannerOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-[#FF5F1F] text-[#FF5F1F] hover:bg-[#FF5F1F]/10 flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4"
                  data-testid="planner-btn"
                >
                  <Route className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">Planner</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-[#262626] w-[95vw] max-w-xl max-h-[90vh] overflow-y-auto mx-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Teko'] text-2xl text-white uppercase flex items-center gap-2">
                    <Route className="w-6 h-6 text-[#FF5F1F]" />
                    {t('trips.planner.title')}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Favorites Toggle */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setShowFavorites(!showFavorites)}
                      className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                        showFavorites 
                          ? 'bg-[#FF5F1F] text-white' 
                          : 'bg-[#121212] text-[#9CA3AF] hover:text-white border border-[#262626]'
                      }`}
                    >
                      <Star className="w-4 h-4" />
                      {t('trips.planner.favorites')} ({favoriteRoutes.length})
                    </button>
                  </div>

                  {/* Favorites List */}
                  {showFavorites && favoriteRoutes.length > 0 && (
                    <div className="bg-[#121212] border border-[#262626] p-3 space-y-2 max-h-48 overflow-y-auto">
                      {favoriteRoutes.map((fav) => (
                        <div 
                          key={fav.id} 
                          className="flex items-center justify-between p-2 bg-black/30 hover:bg-[#FF5F1F]/10 transition-colors cursor-pointer group"
                          onClick={() => loadFavoriteToPlanner(fav)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{fav.nome}</p>
                            <p className="text-[#9CA3AF] text-xs truncate">
                              {fav.start_name.split(',')[0]} → {fav.end_name.split(',')[0]}
                            </p>
                            <p className="text-[#FF5F1F] text-xs font-mono">
                              {fav.distance_km} km • {fav.duration_hours}h
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteFavoriteRoute(fav.id); }}
                            className="p-1 text-[#9CA3AF] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {showFavorites && favoriteRoutes.length === 0 && (
                    <div className="bg-[#121212] border border-[#262626] p-4 text-center">
                      <Star className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                      <p className="text-[#9CA3AF] text-sm">{t('trips.planner.noFavorites')}</p>
                    </div>
                  )}

                  {/* Start Location */}
                  <div className="relative">
                    <Label className="form-label flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      {t('trips.planner.start')}
                    </Label>
                    <div className="relative">
                      <Input
                        value={startQuery}
                        onChange={(e) => {
                          setStartQuery(e.target.value);
                          searchLocation(e.target.value, 'start');
                        }}
                        placeholder={t('trips.planner.searchPlace')}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 pr-10"
                      />
                      {searchingStart && (
                        <Loader2 className="absolute right-3 top-3 w-5 h-5 animate-spin text-[#9CA3AF]" />
                      )}
                    </div>
                    {startResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-[#121212] border border-[#262626] max-h-48 overflow-auto">
                        {startResults.map((r, i) => (
                          <button
                            key={i}
                            onClick={() => selectLocation(r, 'start')}
                            className="w-full px-3 py-2 text-left text-white hover:bg-[#FF5F1F]/20 text-sm"
                          >
                            <MapPin className="w-3 h-3 inline mr-2 text-[#FF5F1F]" />
                            {r.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Waypoints (Tappe) */}
                  <div className="space-y-2">
                    <Label className="form-label flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      {t('trips.planner.waypoints')}
                    </Label>
                    
                    {/* Existing waypoints */}
                    {waypoints.length > 0 && (
                      <div className="space-y-1">
                        {waypoints.map((wp, i) => (
                          <div key={i} className="flex items-center gap-2 bg-[#121212] px-3 py-2 border border-[#262626]">
                            <span className="text-yellow-500 font-mono text-xs">{i + 1}</span>
                            <span className="text-white text-sm flex-1 truncate">{wp.name}</span>
                            <button
                              onClick={() => removeWaypoint(i)}
                              className="text-[#9CA3AF] hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add waypoint input */}
                    <div className="relative">
                      <Input
                        value={waypointQuery}
                        onChange={(e) => {
                          setWaypointQuery(e.target.value);
                          searchLocation(e.target.value, 'waypoint');
                        }}
                        placeholder={t('trips.planner.addWaypoint')}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 pr-10"
                      />
                      {searchingWaypoint && (
                        <Loader2 className="absolute right-3 top-3 w-5 h-5 animate-spin text-[#9CA3AF]" />
                      )}
                    </div>
                    {waypointResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-[#121212] border border-[#262626] max-h-48 overflow-auto">
                        {waypointResults.map((r, i) => (
                          <button
                            key={i}
                            onClick={() => selectLocation(r, 'waypoint')}
                            className="w-full px-3 py-2 text-left text-white hover:bg-[#FF5F1F]/20 text-sm"
                          >
                            <MapPin className="w-3 h-3 inline mr-2 text-yellow-500" />
                            {r.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* End Location */}
                  <div className="relative">
                    <Label className="form-label flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      {t('trips.planner.end')}
                    </Label>
                    <div className="relative">
                      <Input
                        value={endQuery}
                        onChange={(e) => {
                          setEndQuery(e.target.value);
                          searchLocation(e.target.value, 'end');
                        }}
                        placeholder={t('trips.planner.searchPlace')}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 pr-10"
                      />
                      {searchingEnd && (
                        <Loader2 className="absolute right-3 top-3 w-5 h-5 animate-spin text-[#9CA3AF]" />
                      )}
                    </div>
                    {endResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-[#121212] border border-[#262626] max-h-48 overflow-auto">
                        {endResults.map((r, i) => (
                          <button
                            key={i}
                            onClick={() => selectLocation(r, 'end')}
                            className="w-full px-3 py-2 text-left text-white hover:bg-[#FF5F1F]/20 text-sm"
                          >
                            <MapPin className="w-3 h-3 inline mr-2 text-[#FF5F1F]" />
                            {r.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Avoid Highways Toggle */}
                  <div className="flex items-center gap-3 py-2">
                    <button
                      onClick={() => setAvoidHighways(!avoidHighways)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        avoidHighways ? 'bg-[#FF5F1F]' : 'bg-[#262626]'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        avoidHighways ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                    <span className="text-white text-sm">{t('trips.planner.avoidHighways')}</span>
                  </div>

                  {/* Calculate Button */}
                  <Button
                    onClick={calculateRoute}
                    disabled={!startCoords || !endCoords || calculating}
                    className="w-full bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
                  >
                    {calculating ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Calculator className="w-4 h-4 mr-2" />
                    )}
                    {t('trips.planner.calculate')}
                  </Button>

                  {/* Route Result */}
                  {routeResult && (
                    <div className="bg-[#121212] border border-[#262626] p-4 space-y-3">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Navigation className="w-4 h-4 text-[#FF5F1F]" />
                        {t('trips.planner.result')}
                      </h3>
                      
                      {/* Interactive Map */}
                      <RouteMap 
                        startCoords={startCoords}
                        endCoords={endCoords}
                        geometry={routeResult.geometry}
                        className="border border-[#262626]"
                      />
                      
                      <div className="grid grid-cols-3 gap-2 sm:gap-4">
                        <div className="text-center">
                          <p className="text-[#FF5F1F] font-mono text-lg sm:text-2xl">{formatNumber(routeResult.distance_km)}</p>
                          <p className="text-[#9CA3AF] text-xs uppercase">km</p>
                        </div>
                        <div className="text-center">
                          <p className="text-white font-mono text-lg sm:text-2xl">{routeResult.duration_hours}h</p>
                          <p className="text-[#9CA3AF] text-xs uppercase">{t('trips.planner.duration')}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-green-400 font-mono text-lg sm:text-2xl">
                            ~{formatNumber(Math.round(routeResult.distance_km * FUEL_COST_PER_KM))}
                          </p>
                          <p className="text-[#9CA3AF] text-xs uppercase">{t('trips.planner.fuelCost')} (€)</p>
                        </div>
                      </div>
                      
                      {/* Save as Favorite */}
                      <div className="flex gap-2">
                        <Input
                          value={favoriteName}
                          onChange={(e) => setFavoriteName(e.target.value)}
                          placeholder={t('trips.planner.favoriteName')}
                          className="flex-1 bg-black border-[#262626] text-white"
                        />
                        <Button
                          onClick={saveFavoriteRoute}
                          disabled={savingFavorite}
                          variant="outline"
                          className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                        >
                          {savingFavorite ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Star className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      
                      <Button
                        onClick={applyRouteToForm}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('trips.planner.createTrip')}
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Trip Button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => handleOpenDialog()}
                  className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4"
                  data-testid="add-viaggio-btn"
                >
                  <Plus className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">Nuovo</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-[#262626] w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto mx-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                    {editingId ? t('trips.editTrip') : t('trips.addTrip')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="form-label">{t('moto.title')}</Label>
                    <Select value={formData.moto_id} onValueChange={(v) => setFormData({...formData, moto_id: v})}>
                      <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11">
                        <SelectValue placeholder={t('trips.selectMoto')} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                        {motoList.map(moto => (
                          <SelectItem key={moto.id} value={moto.id}>
                            {moto.marca} {moto.modello}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="form-label">{t('trips.tripTitle')}</Label>
                    <Input
                      value={formData.titolo}
                      onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="viaggio-titolo"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">{t('trips.departure')}</Label>
                      <Input
                        value={formData.partenza}
                        onChange={(e) => setFormData({...formData, partenza: e.target.value})}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                        data-testid="viaggio-partenza"
                      />
                    </div>
                    <div>
                      <Label className="form-label">{t('trips.arrival')}</Label>
                      <Input
                        value={formData.arrivo}
                        onChange={(e) => setFormData({...formData, arrivo: e.target.value})}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                        data-testid="viaggio-arrivo"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">{t('trips.departureDate')}</Label>
                      <Input
                        type="date"
                        value={formData.data_partenza}
                        onChange={(e) => setFormData({...formData, data_partenza: e.target.value})}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="viaggio-data-partenza"
                      />
                    </div>
                    <div>
                      <Label className="form-label">{t('trips.arrivalDate')}</Label>
                      <Input
                        type="date"
                        value={formData.data_arrivo}
                        onChange={(e) => setFormData({...formData, data_arrivo: e.target.value})}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="viaggio-data-arrivo"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">{t('trips.kmTraveled')}</Label>
                      <Input
                        type="number"
                        value={formData.km_percorsi}
                        onChange={(e) => setFormData({...formData, km_percorsi: parseInt(e.target.value) || 0})}
                        onFocus={(e) => e.target.select()}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="viaggio-km"
                      />
                    </div>
                    <div>
                      <Label className="form-label">{t('trips.duration')}</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.durata_ore}
                        onChange={(e) => setFormData({...formData, durata_ore: parseFloat(e.target.value) || 0})}
                        onFocus={(e) => e.target.select()}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="viaggio-durata"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">{t('trips.cost')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.costo || ''}
                        onChange={(e) => setFormData({...formData, costo: e.target.value ? parseFloat(e.target.value) : null})}
                        onFocus={(e) => e.target.select()}
                        placeholder="0.00"
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="viaggio-costo"
                      />
                    </div>
                    <div>
                      <Label className="form-label">{t('trips.currency')}</Label>
                      <Select 
                        value={formData.valuta} 
                        onValueChange={(v) => setFormData({...formData, valuta: v})}
                      >
                        <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="viaggio-valuta-select">
                          <SelectValue placeholder="Valuta" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                          {VALUTE.map(v => (
                            <SelectItem key={v.code} value={v.code}>
                              {v.symbol} - {v.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">{t('trips.photos')}</Label>
                    <FotoUploader
                      fotoUrls={formData.foto_urls}
                      onChange={(urls) => setFormData({...formData, foto_urls: urls})}
                      maxFiles={10}
                    />
                  </div>

                  <div>
                    <Label className="form-label">{t('trips.diary')}</Label>
                    <Textarea
                      value={formData.diario}
                      onChange={(e) => setFormData({...formData, diario: e.target.value})}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none min-h-[100px]"
                      placeholder={t('trips.diaryPlaceholder')}
                      data-testid="viaggio-diario"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setDialogOpen(false); resetForm(); }}
                      className="flex-1 border-[#262626] hover:bg-[#262626]"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="flex-1 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <ListSkeleton count={4} Component={ViaggioCardSkeleton} />
        ) : viaggi.length === 0 ? (
          <div className="vault-card text-center py-12">
            <Map className="w-16 h-16 text-[#FF5F1F]/30 mx-auto mb-4" />
            <p className="text-[#9CA3AF] text-lg mb-2">{t('trips.noTrips')}</p>
            <p className="text-[#6B7280] text-sm">{t('trips.startDocumenting')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {viaggi.map((viaggio) => (
              <div key={viaggio.id} className="vault-card" data-testid={`viaggio-${viaggio.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#FF5F1F] font-mono text-sm px-2 py-0.5 bg-[#FF5F1F]/10 border border-[#FF5F1F]/30">
                        {getMotoName(viaggio.moto_id)}
                      </span>
                      <h3 className="text-white font-semibold text-lg">{viaggio.titolo}</h3>
                    </div>
                    <div className="flex items-center gap-2 text-[#9CA3AF]">
                      <MapPin className="w-4 h-4 text-green-500" />
                      <span>{viaggio.partenza}</span>
                      <span className="text-[#FF5F1F]">→</span>
                      <MapPin className="w-4 h-4 text-red-500" />
                      <span>{viaggio.arrivo}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-[#FF5F1F] font-mono text-lg">{formatNumber(viaggio.km_percorsi)}</p>
                        <p className="text-[#9CA3AF] text-xs uppercase">km</p>
                      </div>
                      <div className="text-center">
                        <p className="text-white font-mono">{viaggio.durata_ore}h</p>
                        <p className="text-[#9CA3AF] text-xs uppercase">{t('trips.planner.duration')}</p>
                      </div>
                      {viaggio.costo != null && viaggio.costo > 0 && (
                        <div className="text-center">
                          <p className="text-green-400 font-mono">
                            {VALUTE.find(v => v.code === viaggio.valuta)?.symbol || '€'}{formatNumber(viaggio.costo)}
                          </p>
                          <p className="text-[#9CA3AF] text-xs uppercase">{t('trips.cost')}</p>
                        </div>
                      )}
                      <div className="text-center hidden sm:block">
                        <p className="text-white font-mono text-sm">{viaggio.data_partenza}</p>
                        <p className="text-[#9CA3AF] text-xs uppercase">{t('trips.departureDate')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openGoogleMaps(viaggio.partenza, viaggio.arrivo)}
                        className="text-[#FF5F1F] hover:text-[#FF5F1F]/80"
                        title={t('trips.openMaps')}
                      >
                        <Navigation className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedViaggio(expandedViaggio === viaggio.id ? null : viaggio.id)}
                      >
                        {expandedViaggio === viaggio.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(viaggio)}
                        className="text-[#9CA3AF] hover:text-white"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(viaggio.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {expandedViaggio === viaggio.id && (
                  <div className="mt-4 pt-4 border-t border-[#262626]">
                    {viaggio.foto_urls && viaggio.foto_urls.length > 0 && (
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
                        {viaggio.foto_urls.map((url, idx) => (
                          <div 
                            key={idx}
                            className="relative cursor-pointer group/img"
                            onClick={() => openLightbox(viaggio.foto_urls, idx)}
                          >
                            <img 
                              src={getImageUrl(url)} 
                              alt="" 
                              className="w-full h-16 object-cover border border-[#262626]" 
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                              <ZoomIn className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {viaggio.diario && (
                      <div className="mb-4">
                        <h4 className="text-[#9CA3AF] text-xs uppercase mb-2">{t('trips.diary')}</h4>
                        <p className="text-white whitespace-pre-wrap">{viaggio.diario}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </Layout>
  );
}
